import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { restoreSession } from './api/client.js'
import { refreshFollowUps } from './glitime/followups.js'
import { refreshLines } from './glitime/lines.js'
import { refreshPromises } from './glitime/promises.js'
import { installScaleAnnouncer } from './glitime/scale.js'
import { Login } from './screens/Login.js'
import { BootSplash } from './shell/BootSplash.js'
import { Shell } from './shell/Shell.js'
import { unlockAudioOnGesture } from './sound/context.js'
import { stopBedWhenHidden } from './sound/engine.js'
import { loadSoundSetting } from './sound/settings.js'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The local database answers instantly and the network is a bonus, so
      // refetching on every focus buys nothing and costs battery.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
})

type Session = 'restoring' | 'anonymous' | 'authenticated'

/**
 * How long the app has to have been in the background before coming back
 * earns an animation. An installed PWA is backgrounded every time you glance
 * at a message, and a splash on each of those would be unbearable; after a few
 * minutes away, a beat of branding reads as the app waking up instead.
 */
const RESUME_SPLASH_AFTER_MS = 3 * 60_000

export function App() {
  const [session, setSession] = useState<Session>('restoring')
  // A fresh module evaluation is a cold start, so the full sequence is the
  // state this mounts in. Everything after it is a resume.
  const [boot, setBoot] = useState<'full' | 'short' | null>('full')

  useEffect(() => {
    void restoreSession().then((ok) => {
      setSession(ok ? 'authenticated' : 'anonymous')
      // Her promise cache: promises are made in chat and broken at log time,
      // so boot and chat-close are the only refresh moments needed. Offline
      // this resolves to the cached copy doing the work, silently.
      if (ok) {
        void refreshPromises()
        void refreshFollowUps()
        void refreshLines()
      }
    })
  }, [])

  // Sound — the scanner's beep and her cues alike — needs an audio context
  // that a gesture has already touched, and WebKit gives a page exactly one
  // chance at that: the first one. Getting anywhere in this app takes several
  // taps, so spend the first.
  useEffect(unlockAudioOnGesture, [])

  // Whether she is allowed to make a sound at all. Read once; a cue fires from
  // inside an event handler and cannot await Dexie to find out.
  useEffect(() => {
    void loadSoundSetting()
  }, [])

  // A phone that goes to sleep with a performance open must not keep humming
  // in a pocket.
  useEffect(stopBedWhenHidden, [])

  // Her word when a scale reading reaches this phone through sync — see
  // `glitime/scale.ts`. Lives here so it is listening whichever tab is up.
  useEffect(installScaleAnnouncer, [])

  useEffect(() => {
    let hiddenAt = 0
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }
      const away = hiddenAt === 0 ? 0 : Date.now() - hiddenAt
      hiddenAt = 0
      if (away < RESUME_SPLASH_AFTER_MS) return
      // Never interrupt a sequence already running, and never restart one.
      setBoot((current) => current ?? 'short')
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      {/* Mounted under the splash rather than after it: the cold-start
          sequence ends by flying the mark onto Today's ring, which has to
          exist and be laid out before it can be measured. */}
      {session === 'authenticated' && (
        <Shell bootPending={boot != null} onSignedOut={() => setSession('anonymous')} />
      )}
      {session === 'anonymous' && <Login onSignedIn={() => setSession('authenticated')} />}
      {boot != null && (
        <BootSplash mode={boot} ready={session !== 'restoring'} onDone={() => setBoot(null)} />
      )}
    </QueryClientProvider>
  )
}
